import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { NiboRepository } from '@/services/nibo/nibo.repository';
import type { NiboEntryDataInput, CreateSupplierInput } from '@/services/nibo/nibo.types';

const repository = new NiboRepository();

// === Keys ===

export const niboKeys = {
  all: ['nibo'] as const,
  integration: (customerId: string) => [...niboKeys.all, 'integration', customerId] as const,
  costCenters: (customerId: string) => [...niboKeys.all, 'cost-centers', customerId] as const,
  financialAccounts: (customerId: string) => [...niboKeys.all, 'financial-accounts', customerId] as const,
  expenseCategories: (customerId: string) => [...niboKeys.all, 'expense-categories', customerId] as const,
  suppliers: (customerId: string) => [...niboKeys.all, 'suppliers', customerId] as const,
  entryData: (entryId: string) => [...niboKeys.all, 'entry-data', entryId] as const,
};

// === Integration ===

export const useNiboIntegration = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: niboKeys.integration(customerId),
    queryFn: async () => {
      try {
        return await repository.getIntegration(customerId);
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!customerId && enabled,
    retry: false, // Don't retry if 404 (not integrated)
  });
};

export const useActivateNibo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, apiToken }: { customerId: string; apiToken: string }) =>
      repository.activate(customerId, apiToken),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: niboKeys.integration(customerId) });
    },
  });
};

export const useDeactivateNibo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ integrationId }: { integrationId: string }) =>
      repository.deactivate(integrationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: niboKeys.all });
    },
  });
};

// === Master Data ===

export const useNiboCostCenters = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: niboKeys.costCenters(customerId),
    queryFn: () => repository.getCostCenters(customerId),
    enabled: !!customerId && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useNiboFinancialAccounts = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: niboKeys.financialAccounts(customerId),
    queryFn: () => repository.getFinancialAccounts(customerId),
    enabled: !!customerId && enabled,
    staleTime: 1000 * 60 * 5,
  });
};

export const useNiboExpenseCategories = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: niboKeys.expenseCategories(customerId),
    queryFn: () => repository.getCategories(customerId),
    enabled: !!customerId && enabled,
    staleTime: 1000 * 60 * 5,
  });
};

export const useNiboSuppliers = (customerId: string, enabled = true) => {
  return useQuery({
    queryKey: niboKeys.suppliers(customerId),
    queryFn: () => repository.getSuppliers(customerId),
    enabled: !!customerId && enabled,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateNiboSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSupplierInput) => repository.createSupplier(input),
    onSuccess: (_, input) => {
      queryClient.invalidateQueries({ queryKey: niboKeys.suppliers(input.customerId) });
    },
  });
};

// === Entry Data ===

export const useNiboEntryData = (entryId: string, enabled = true) => {
  return useQuery({
    queryKey: niboKeys.entryData(entryId),
    queryFn: () => repository.getEntryData(entryId),
    enabled: !!entryId && enabled,
    retry: false,
  });
};

export const useUpsertNiboEntryData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, data }: { entryId: string; data: NiboEntryDataInput }) =>
      repository.upsertEntryData(entryId, data),
    onSuccess: (_, { entryId }) => {
      queryClient.invalidateQueries({ queryKey: niboKeys.entryData(entryId) });
    },
  });
};

// === Sync ===

export const useSyncNiboEntry = () => {
  return useMutation({
    mutationFn: ({ entryId, customerId }: { entryId: string; customerId: string }) =>
      repository.syncEntry(entryId, customerId),
  });
};
